import React, { useMemo, memo, useRef, useEffect, useState } from "react";
import { DebugStep, VariableInfo } from "../../types";
import { Eyebrow } from "../atoms/eyebrow";
import { DisplayHeading } from "../atoms/display-heading";
import { IconButton } from "../atoms/icon-button";
import { SearchInput } from "../molecules/search-input";

interface FrameInspectorProps {
	debugStep: DebugStep | null;
	isDebugging: boolean;
	currentStep: number;
	totalSteps: number;
	title?: React.ReactNode;
	fontSize?: number;
	isExpanded?: boolean;
	onToggleExpand?: () => void;
}

// Helper to get icon for variable type
const TypeIcon = ({ type }: { type: string }) => {
	switch (type) {
		case "int":
		case "float":
		case "complex":
			return (
				<span className="text-blue-600 dark:text-blue-400 font-mono font-bold text-[10px] bg-blue-100 dark:bg-blue-900/30 px-1 rounded flex items-center justify-center h-5 min-w-[1.25rem]">
					#
				</span>
			);
		case "str":
			return (
				<span className="text-green-600 dark:text-green-400 font-mono font-bold text-[10px] bg-green-100 dark:bg-green-900/30 px-1 rounded flex items-center justify-center h-5 min-w-[1.25rem]">
					Az
				</span>
			);
		case "bool":
			return (
				<span className="text-purple-600 dark:text-purple-400 font-mono font-bold text-[10px] bg-purple-100 dark:bg-purple-900/30 px-1 rounded flex items-center justify-center h-5 min-w-[1.25rem]">
					T/F
				</span>
			);
		case "list":
		case "tuple":
		case "set":
			return (
				<span className="text-orange-600 dark:text-orange-400 font-mono font-bold text-[10px] bg-orange-100 dark:bg-orange-900/30 px-1 rounded flex items-center justify-center h-5 min-w-[1.25rem]">
					[ ]
				</span>
			);
		case "dict":
			return (
				<span className="text-red-600 dark:text-red-400 font-mono font-bold text-[10px] bg-red-100 dark:bg-red-900/30 px-1 rounded flex items-center justify-center h-5 min-w-[1.25rem]">{`{ }`}</span>
			);
		case "function":
		case "method":
			return (
				<span className="text-indigo-600 dark:text-indigo-400 font-mono font-bold text-[10px] bg-indigo-100 dark:bg-indigo-900/30 px-1 rounded flex items-center justify-center h-5 min-w-[1.25rem]">
					fn
				</span>
			);
		case "NoneType":
			return (
				<span className="text-gray-500 dark:text-gray-400 font-mono font-bold text-[10px] bg-gray-100 dark:bg-gray-800 px-1 rounded flex items-center justify-center h-5 min-w-[1.25rem]">
					∅
				</span>
			);
		default:
			return (
				<span className="text-slate-500 dark:text-slate-400 font-mono font-bold text-[10px] bg-slate-100 dark:bg-slate-800 px-1 rounded flex items-center justify-center h-5 min-w-[1.25rem]">
					?
				</span>
			);
	}
};

// Memoized variable row component for performance
const VariableRow = memo<{
	variable: VariableInfo;
	index: number;
	fontSize: number;
}>(({ variable, index, fontSize }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [showCopy, setShowCopy] = useState(false);
	const [copied, setCopied] = useState(false);

	// Reset expanded state when variable info changes (e.g. step change if the object is different,
	// but usually we want to keep expansion state if it's the same variable.
	// Since we don't track variable identity perfectly across steps, we'll reset if value creates a new layout need).
	// Actually, for better UX, let's keep it simple: collapse on value change is safest to avoid weird rendering.
	useEffect(() => {
		setIsExpanded(false);
	}, [variable.value, variable.id]);

	// Reset copied state after 2 seconds
	useEffect(() => {
		if (copied) {
			const timer = setTimeout(() => setCopied(false), 2000);
			return () => clearTimeout(timer);
		}
	}, [copied]);

	const handleCopy = (e: React.MouseEvent) => {
		e.stopPropagation();
		navigator.clipboard.writeText(variable.value);
		setCopied(true);
	};

	const getTypeColor = useMemo(() => {
		switch (variable.type) {
			case "int":
			case "float":
				return "text-blue-600 dark:text-blue-400";
			case "str":
				return "text-green-700 dark:text-green-400";
			case "bool":
				return "text-purple-600 dark:text-purple-400";
			case "list":
			case "tuple":
				return "text-orange-700 dark:text-orange-400";
			case "dict":
			case "set":
				return "text-red-700 dark:text-red-400";
			case "function":
				return "text-indigo-600 dark:text-indigo-400";
			case "NoneType":
				return "text-gray-500 dark:text-gray-400 italic";
			default:
				return "text-slate-700 dark:text-slate-300";
		}
	}, [variable.type]);

	const { displayValue, needsTruncation } = useMemo(() => {
		const fullValue = variable.value;
		const maxLength = 80;

		if (fullValue.length > maxLength && !isExpanded) {
			return {
				displayValue: fullValue.substring(0, maxLength) + "...",
				needsTruncation: true,
			};
		}

		return {
			displayValue: fullValue,
			needsTruncation: false,
		};
	}, [variable.value, isExpanded]);

	const handleToggleExpand = () => {
		if (needsTruncation || isExpanded) {
			setIsExpanded(!isExpanded);
		}
	};

	return (
		<div
			className="group flex flex-col py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors px-3 rounded-lg -mx-2"
			onMouseEnter={() => setShowCopy(true)}
			onMouseLeave={() => setShowCopy(false)}
		>
			<div className="flex items-start gap-3">
				{/* Left: Icon and Name */}
				<div className="flex items-center gap-2 min-w-[120px] max-w-[40%] flex-shrink-0 pt-0.5">
					<TypeIcon type={variable.type} />
					<div
						className="font-mono font-medium text-gray-700 dark:text-gray-200 truncate select-all"
						style={{ fontSize: `${fontSize}px` }}
						title={variable.name}
					>
						{variable.name}
					</div>
				</div>

				{/* Right: Value */}
				<div className="flex-1 min-w-0 relative">
					<div
						className={`font-mono leading-relaxed break-all cursor-pointer ${getTypeColor} ${isExpanded ? "whitespace-pre-wrap" : ""}`}
						style={{ fontSize: `${fontSize}px` }}
						onClick={handleToggleExpand}
					>
						{displayValue}
					</div>

					{/* Action buttons (Copy/Expand) */}
					<div
						className={`absolute -right-2 top-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg px-1 ${showCopy ? "opacity-100" : ""}`}
					>
						<button
							onClick={handleCopy}
							className="p-1 text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400 transition-colors"
							title="Copy value"
						>
							{copied ? (
								<svg
									className="w-3.5 h-3.5 text-green-500"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M5 13l4 4L19 7"
									/>
								</svg>
							) : (
								<svg
									className="w-3.5 h-3.5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
									/>
								</svg>
							)}
						</button>
						{needsTruncation && (
							<button
								onClick={(e) => {
									e.stopPropagation();
									setIsExpanded(!isExpanded);
								}}
								className="p-1 text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400 transition-colors"
								title={isExpanded ? "Collapse" : "Expand"}
							>
								<svg
									className={`w-3.5 h-3.5 transform transition-transform ${isExpanded ? "rotate-180" : ""}`}
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M19 9l-7 7-7-7"
									/>
								</svg>
							</button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
});

VariableRow.displayName = "VariableRow";

// Memoized frame component
const Frame = memo<{
	frameName: string;
	variables: Record<string, VariableInfo>;
	isActive: boolean;
	isGlobal: boolean;
	currentDebugStep?: DebugStep | null;
	fontSize: number;
	filterText: string;
}>(
	({
		frameName,
		variables,
		isActive,
		isGlobal,
		currentDebugStep,
		fontSize,
		filterText,
	}) => {
		const variableList = useMemo(() => {
			let vars = Object.values(variables);
			if (filterText) {
				const lowerFilter = filterText.toLowerCase();
				vars = vars.filter(
					(v) =>
						v.name.toLowerCase().includes(lowerFilter) ||
						v.value.toLowerCase().includes(lowerFilter) ||
						v.type.toLowerCase().includes(lowerFilter),
				);
			}
			// Sort variables: special ones like __name__ last, others alphabetical
			return vars.sort((a, b) => {
				if (a.name.startsWith("__") && !b.name.startsWith("__")) return 1;
				if (!a.name.startsWith("__") && b.name.startsWith("__")) return -1;
				return a.name.localeCompare(b.name);
			});
		}, [variables, filterText]);

		const frameIcon = useMemo(() => {
			if (isGlobal) {
				return (
					<div className="w-6 h-6 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center text-purple-600 dark:text-purple-400 shadow-sm border border-purple-200 dark:border-purple-800/50">
						<span className="font-bold text-xs">G</span>
					</div>
				);
			}
			return (
				<div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-blue-600 dark:text-blue-400 shadow-sm border border-blue-200 dark:border-blue-800/50">
					<span className="font-bold text-xs">ƒ</span>
				</div>
			);
		}, [isGlobal]);

		// Don't show frame if filtering hides all variables, unless it's the active frame or Main Module with no filter
		if (filterText && variableList.length === 0 && !isActive) return null;

		return (
			<div
				className={`mb-4 rounded-xl border transition-all duration-300 overflow-hidden ${
					isActive
						? "border-blue-400/50 dark:border-blue-500/50 bg-white dark:bg-gray-800 shadow-md ring-2 ring-blue-500/10"
						: "border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50 hover:bg-white dark:hover:bg-gray-800 shadow-sm hover:shadow"
				}`}
				data-active={isActive}
			>
				{/* Frame Header */}
				<div
					className={`px-4 py-3 border-b flex items-center justify-between sticky top-0 bg-opacity-95 backdrop-blur-sm z-10 ${
						isActive
							? "bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-800/30"
							: "bg-gray-50/80 dark:bg-gray-800/80 border-gray-100 dark:border-gray-700"
					}`}
				>
					<div className="flex items-center gap-3 overflow-hidden">
						{frameIcon}
						<div className="flex-1 min-w-0">
							<div
								className={`text-sm font-bold truncate flex items-center gap-2 ${isActive ? "text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-300"}`}
							>
								{frameName}
								{isActive && (
									<span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 animate-pulse">
										Active
									</span>
								)}
							</div>
						</div>
					</div>

					{/* Variable count badge */}
					<div
						className={`px-2 py-0.5 rounded-md text-xs font-medium border ${
							isActive
								? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
								: "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600"
						}`}
					>
						{variableList.length} vars
					</div>
				</div>

				{/* Variables */}
				<div className="px-4 py-2 bg-white dark:bg-gray-800/50 min-h-[50px]">
					{variableList.length > 0 ? (
						<div className="flex flex-col gap-1">
							{variableList.map((variable, index) => (
								<VariableRow
									key={`${variable.name}-${variable.id || index}`}
									variable={variable}
									index={index}
									fontSize={fontSize}
								/>
							))}
						</div>
					) : (
						<div className="text-center py-6 text-gray-400 dark:text-gray-500">
							{filterText ? (
								<p className="text-sm">No match found</p>
							) : (
								<>
									<div className="w-8 h-8 rounded-full bg-gray-50 dark:bg-gray-700/50 mx-auto mb-2 flex items-center justify-center opacity-70">
										<span className="text-lg">∅</span>
									</div>
									<p className="text-xs font-medium">No variables</p>
								</>
							)}
						</div>
					)}
				</div>
			</div>
		);
	},
);

Frame.displayName = "Frame";

const FrameInspector: React.FC<FrameInspectorProps> = ({
	debugStep,
	isDebugging,
	currentStep,
	totalSteps,
	title = "Inspector",
	fontSize = 13,
	isExpanded = false,
	onToggleExpand,
}) => {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [filterText, setFilterText] = useState("");

	// Memoize frame data processing
	const frameData = useMemo(() => {
		if (!debugStep) return null;

		const globalVars = debugStep.globalVars;
		const hasGlobalVars = Object.keys(globalVars).length > 0;
		const mainModuleFrame = debugStep.stack.find(
			(frame) => frame.function === "<module>",
		);
		const isMainModuleActive = mainModuleFrame?.isCurrentFrame || false;

		const mainFrameVars = mainModuleFrame
			? { ...globalVars, ...mainModuleFrame.locals }
			: globalVars;

		const functionFrames = debugStep.stack.filter(
			(frame) => frame.function !== "<module>",
		);

		const allFrames: Array<{
			type: "main" | "function";
			frameName: string;
			variables: Record<string, VariableInfo>;
			isActive: boolean;
			isGlobal: boolean;
			originalFrame?: any;
		}> = [];

		// Add main module frame
		if (hasGlobalVars || mainModuleFrame) {
			allFrames.push({
				type: "main",
				frameName: "Global Scope",
				variables: mainFrameVars,
				isActive: isMainModuleActive,
				isGlobal: true,
			});
		}

		// Add function frames
		functionFrames.forEach((frame) => {
			allFrames.push({
				type: "function",
				frameName: `${frame.function}()`,
				variables: frame.locals,
				isActive: frame.isCurrentFrame,
				isGlobal: false,
				originalFrame: frame,
			});
		});

		// Sort frames: Active first, then by recursion depth (usually reverse of stack for display)
		const sortedFrames = allFrames.sort((a, b) => {
			if (a.isActive) return -1;
			if (b.isActive) return 1;
			return 0; // Keep stack order otherwise
		});

		return {
			sortedFrames,
			returnValue: debugStep.returnValue,
		};
	}, [debugStep]);

	// Auto-scroll to active frame
	useEffect(() => {
		if (scrollContainerRef.current && frameData && isDebugging) {
			const activeFrameElement = scrollContainerRef.current.querySelector(
				'[data-active="true"]',
			);
			if (activeFrameElement) {
				activeFrameElement.scrollIntoView({
					behavior: "smooth",
					block: "start",
					inline: "nearest",
				});
			}
		}
	}, [currentStep, frameData, isDebugging]);

	if (!isDebugging || !debugStep || !frameData) {
		return (
			<div className="h-full flex flex-col bg-white/95 dark:bg-[#0c0c0e]/95 border-l border-gray-200/80 dark:border-white/[0.06] relative overflow-hidden">
				<div className="panel-header flex-shrink-0 !py-2.5">
					<div className="flex items-baseline gap-2">
						<Eyebrow className="!text-[9px]">Inspector</Eyebrow>
						<DisplayHeading size="sm" className="!text-[15px]">
							Variables
						</DisplayHeading>
					</div>
				</div>

				<div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
					<DisplayHeading
						size="xl"
						className="!text-gray-300 dark:!text-gray-700 mb-3"
					>
						Idle.
					</DisplayHeading>
					<p className="text-[11px] text-gray-500 dark:text-gray-500 max-w-[220px] leading-relaxed">
						Press{" "}
						<span className="text-orange-700 dark:text-amber-300 font-medium">
							Learn Mode
						</span>{" "}
						to step through your code and watch variables, stacks, and the
						execution flow update live.
					</p>
				</div>
			</div>
		);
	}

	const { sortedFrames, returnValue } = frameData;

	return (
		<div className="h-full flex flex-col bg-white/95 dark:bg-[#0c0c0e]/95 border-l border-gray-200/80 dark:border-white/[0.06] backdrop-blur-md transition-colors duration-200">
			<div className="flex-shrink-0 px-3.5 py-2.5 border-b border-gray-200/80 dark:border-white/[0.06] bg-white/80 dark:bg-[#0c0c0e]/80 z-20">
				<div className="flex items-center justify-between mb-2.5">
					<div className="flex items-baseline gap-2 min-w-0">
						<Eyebrow className="!text-[9px]">Inspector</Eyebrow>
						<DisplayHeading size="sm" className="!text-[15px]">
							Variables
						</DisplayHeading>
						<span className="text-[10px] tabular text-gray-500 dark:text-gray-500">
							{currentStep + 1}/{totalSteps}
						</span>
					</div>

					{onToggleExpand && (
						<IconButton
							size="xs"
							onClick={onToggleExpand}
							title={isExpanded ? "Collapse" : "Expand"}
						>
							<svg
								className="w-3.5 h-3.5"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.7"
								viewBox="0 0 24 24"
							>
								{isExpanded ? (
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M15 15v4.5M15 15h4.5M15 15l5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M9 15H4.5M9 15v4.5M9 15l-5.25 5.25"
									/>
								) : (
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
									/>
								)}
							</svg>
						</IconButton>
					)}
				</div>

				<SearchInput
					value={filterText}
					onChange={(e) => setFilterText(e.target.value)}
					placeholder="Filter variables…"
					hasValue={!!filterText}
					onClear={() => setFilterText("")}
				/>
			</div>

			{/* Frames Content */}
			<div
				ref={scrollContainerRef}
				className="flex-1 overflow-y-auto p-4 space-y-4"
			>
				{/* Return Value Section - highlighted at top if exists */}
				{returnValue && !filterText && (
					<div className="rounded-xl border border-orange-200 dark:border-orange-800/50 bg-gradient-to-r from-orange-50 to-white dark:from-orange-900/10 dark:to-gray-800/50 shadow-sm overflow-hidden">
						<div className="px-4 py-2 border-b border-orange-100 dark:border-orange-800/30 flex items-center gap-2">
							<span className="flex items-center justify-center w-5 h-5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
								<svg
									className="w-3 h-3"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
									/>
								</svg>
							</span>
							<span className="text-xs font-bold text-orange-800 dark:text-orange-300 uppercase tracking-wide">
								Return Value
							</span>
						</div>
						<div className="px-4 py-2">
							<VariableRow
								variable={returnValue}
								index={0}
								fontSize={fontSize}
							/>
						</div>
					</div>
				)}
				{sortedFrames.map((frameInfo, index) => (
					<Frame
						key={
							frameInfo.type === "main"
								? "main-module"
								: `frame-${frameInfo.originalFrame?.function || "unknown"}-${frameInfo.originalFrame?.lineNumber || 0}-${index}`
						}
						frameName={frameInfo.frameName}
						variables={frameInfo.variables}
						isActive={frameInfo.isActive}
						isGlobal={frameInfo.isGlobal}
						currentDebugStep={debugStep}
						fontSize={fontSize}
						filterText={filterText}
					/>
				))}
				<div className="h-4"></div> {/* Bottom spacer */}
			</div>
		</div>
	);
};

export default memo(FrameInspector);
